/**
 * CSV export shaping for the Infrastructure views.
 *
 * Every export in this module is a PURE function from a domain row to a flat
 * `ExportRecord` plus a static {@link ColumnConfig} list, so the shape a user
 * downloads can be unit-tested without a store, a socket, or a DOM. The stores
 * only decide WHICH rows go in (the on-screen set: current filters + sort);
 * they never decide which FIELDS go out — that lives here.
 *
 * ── CREDENTIAL RULE (non-negotiable) ────────────────────────────────────────
 * Infrastructure rows are adjacent to three secrets: the Portainer API key
 * (root-equivalent on a Docker host), the ACR registry password /
 * service-principal secret, and the Azure DevOps PAT. The API encrypts all
 * three at rest and NEVER serialises them — a record carries at most a
 * `hasApiKey` / `hasCredential` presence boolean or a `credentialMasked`
 * string. An export must not weaken that: a CSV leaves the app, gets mailed
 * around, and lands in a shared drive where nobody re-applies RBAC.
 *
 * The defence is structural, not a filter. Each shaper below builds its record
 * by EXPLICITLY NAMING the fields it emits, so an unknown or newly-added field
 * — including one the API starts returning tomorrow — cannot reach a CSV by
 * default. Nothing here spreads (`...row`), iterates `Object.keys(row)`, or
 * reads a field by computed name. `infrastructureExports.test.ts` pins that by
 * shaping rows with credential material injected and asserting neither the key
 * nor the value survives.
 *
 * Presence flags are excluded too. "API key on file: Yes" is not a secret, but
 * it is a map of which hosts are worth attacking, and the value of putting it
 * in a spreadsheet is nil — the Docker Hosts screen already shows it. So the
 * host export carries no credential-derived column at all, and the rule reads
 * as one sentence instead of a judgement call per field.
 */
import type { ColumnConfig } from "shared/common/utils/export";
import type { ClientInstance, DockerHost } from "./infrastructure.types";
// TYPE-ONLY on purpose: InstanceDriftStore imports this module for its export
// columns, so a value import here would close a runtime cycle. `import type` is
// erased at compile time, which keeps the dependency one-directional.
import type { InstanceDrift } from "./DeploymentStatus/InstanceDriftStore";

/** A single flat CSV row. `exportToCsv` reads values by column `key`. */
export type ExportRecord = Record<string, string | number | null>;

/** Human role label, matching the RoleChip/roleLabel the tables render. */
const roleLabel = (role?: string | null): string => {
  if (!role) return "";
  if (role === "api") return "API";
  if (role === "web") return "Web";
  return role;
};

/** Booleans read badly as "true"/"false" in a spreadsheet. */
const yesNo = (value?: boolean | null): string =>
  value === true ? "Yes" : value === false ? "No" : "";

/** Timestamps go out as raw ISO-8601 (UTC), the AuditLog export's convention:
 *  sortable in every spreadsheet and unambiguous across time zones. The
 *  toolkit's `format: "date"` would drop the time, which matters for "last
 *  seen". */
const iso = (value?: string | null): string | null => value ?? null;

// ─────────────────────────────────────────────────────────────────────────────
// Client Instances — the fleet inventory
// ─────────────────────────────────────────────────────────────────────────────

/** CSV shape of the Client Instances grid (see {@link toClientInstanceExportRow}). */
export const CLIENT_INSTANCE_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "client", label: "Client" },
  { key: "role", label: "Role" },
  { key: "status", label: "Status" },
  { key: "health", label: "Health" },
  { key: "enabled", label: "Actions Enabled" },
  { key: "assignment", label: "Assignment" },
  { key: "image", label: "Image" },
  { key: "digest", label: "Running Digest" },
  { key: "host", label: "Host" },
  { key: "container", label: "Container" },
  { key: "lastSeenAt", label: "Last Seen (UTC)" },
  { key: "updateAvailable", label: "Update Available" },
  { key: "latestImage", label: "Latest Image" },
  { key: "instanceId", label: "Instance Id" },
];

/**
 * One fleet-inventory row. `clientLabel` is passed in rather than resolved here
 * because the name lives in the global ClientStore, which a pure function has
 * no business reaching into — the store hands over the same label the grid's
 * group header shows.
 *
 * NOTE the field list is exhaustive and literal: a ClientInstance carries no
 * secret today, and this shaper is what guarantees it still carries none if one
 * is ever added to the type.
 */
export function toClientInstanceExportRow(
  instance: ClientInstance,
  clientLabel: string
): ExportRecord {
  return {
    client: clientLabel,
    role: roleLabel(instance.role),
    status: instance.status ?? "",
    health: instance.health ?? "",
    enabled: yesNo(instance.enabled !== false),
    assignment: instance.assignmentState ?? "",
    image: instance.imageRef ?? "",
    digest: instance.currentDigest ?? "",
    host: instance.dockerHostName ?? "",
    container: instance.containerName ?? "",
    lastSeenAt: iso(instance.lastSeenAt),
    updateAvailable: yesNo(instance.updateAvailable === true),
    latestImage: instance.latestImageRef ?? "",
    instanceId: instance.id,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Status — the drift report
// ─────────────────────────────────────────────────────────────────────────────

/** CSV shape of the Deployment Status table (see {@link toInstanceDriftExportRow}). */
export const INSTANCE_DRIFT_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "instance", label: "Instance" },
  { key: "role", label: "Role" },
  { key: "client", label: "Client" },
  { key: "runningDigest", label: "Running Digest" },
  { key: "expectedDigest", label: "Expected Digest" },
  { key: "driftStatus", label: "Drift Status" },
  { key: "instanceId", label: "Instance Id" },
];

/**
 * One drift row. Digests go out in FULL — the table abbreviates them to twelve
 * characters to fit, but the whole point of exporting a drift report is to
 * paste an exact digest into a release conversation, and a truncated one is
 * worse than none.
 *
 * `clientLabel` and `statusLabel` are passed in rather than resolved here: the
 * client name comes from the global ClientStore and the status label from
 * InstanceDriftStore's own metadata map, and reaching into either would make
 * this function impure (and, for the latter, cyclic).
 */
export function toInstanceDriftExportRow(
  row: InstanceDrift,
  clientLabel: string,
  statusLabel: string
): ExportRecord {
  return {
    instance: row.name ?? row.instanceId,
    role: roleLabel(row.role),
    client: clientLabel,
    runningDigest: row.runningDigest ?? "",
    expectedDigest: row.expectedDigest ?? "",
    driftStatus: statusLabel,
    instanceId: row.instanceId,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Docker Hosts — the host inventory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CSV shape of the Docker Hosts table (see {@link toDockerHostExportRow}).
 *
 * DELIBERATELY MISSING: the screen's "API Key" column. It renders
 * `hasApiKey` — a presence boolean, never the key — but see the credential rule
 * at the top of this file: a spreadsheet listing which Portainer endpoints hold
 * a root-equivalent key is a target list, and it answers no question an
 * inventory export exists to answer.
 */
export const DOCKER_HOST_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "name", label: "Host" },
  { key: "connectionName", label: "Portainer Server" },
  { key: "portainerUrl", label: "Portainer URL" },
  { key: "endpointId", label: "Endpoint" },
  { key: "status", label: "Status" },
  { key: "statusMessage", label: "Status Detail" },
  { key: "containerCount", label: "Containers", format: "number" },
  { key: "runningCount", label: "Running", format: "number" },
  { key: "engineVersion", label: "Engine" },
  { key: "lastSeenAt", label: "Last Seen (UTC)" },
];

export function toDockerHostExportRow(host: DockerHost): ExportRecord {
  return {
    name: host.name ?? "",
    connectionName: host.connectionName ?? "",
    portainerUrl: host.portainerUrl ?? "",
    endpointId: host.endpointId != null ? String(host.endpointId) : "",
    status: host.status ?? "unknown",
    statusMessage: host.statusMessage ?? "",
    containerCount: host.containerCount ?? null,
    runningCount: host.runningCount ?? null,
    engineVersion: host.engineVersion ?? "",
    lastSeenAt: iso(host.lastSeenAt),
  };
}
